import { ArrowLeftRight, Info, Plus, TrendingUp } from "lucide-react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScaledThumbnail } from "./ScaledThumbnail";

interface ScenarioComparisonCardThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

const SCENARIOS = [
  {
    name: "Current Plan",
    description: "Base forecast with committed adjustments",
    status: "active",
    intent: "success" as const,
    categoryDot: "bg-[var(--accent-success-500)]",
    minCash: "£112k",
    coverage: "13.7 months",
    netImpact: "+£24k",
    positiveImpact: true,
  },
  {
    name: "Aggressive Growth",
    description: "Two new hires and expanded marketing",
    status: "pending approval",
    intent: "warning" as const,
    categoryDot: "bg-[var(--accent-success-500)]",
    minCash: "£68k",
    coverage: "8.2 months",
    netImpact: "-£42k",
    positiveImpact: false,
  },
  {
    name: "Conservative",
    description: "Discretionary spend deferred to Q4",
    status: "draft",
    intent: "info" as const,
    categoryDot: "bg-[var(--accent-info-500)]",
    minCash: "£145k",
    coverage: "16.4 months",
    netImpact: "+£31k",
    positiveImpact: true,
  },
];

const INTENT_CARD_CLASS = {
  success:
    "border-l-4 border-l-[var(--accent-success-500)] bg-[var(--accent-success-50)]",
  warning:
    "border-l-4 border-l-[var(--accent-warning-500)] bg-[var(--accent-warning-50)]",
  info: "border-l-4 border-l-[var(--accent-info-500)] bg-[var(--accent-info-50)]",
};

export default function ScenarioComparisonCardThumbnail({
  isHovered = false,
  alwaysVisible = false,
}: ScenarioComparisonCardThumbnailProps) {
  return (
    <ScaledThumbnail
      isHovered={isHovered}
      alwaysVisible={alwaysVisible}
      contentWidth={380}
    >
      <Card className="w-[380px]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Scenarios Comparison
            </CardTitle>
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {SCENARIOS.map((scenario) => (
            <div
              key={scenario.name}
              className={`p-3 rounded-lg ${INTENT_CARD_CLASS[scenario.intent]}`}
            >
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span
                      className={`h-2 w-2 rounded-full flex-shrink-0 ${scenario.categoryDot}`}
                    />
                    <div className="font-semibold text-sm truncate">
                      {scenario.name}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {scenario.description}
                  </div>
                </div>
                <Badge
                  variant={scenario.intent}
                  className="flex-shrink-0 text-xs capitalize"
                >
                  {scenario.status}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-muted-foreground">Min Cash</div>
                  <div className="font-semibold">{scenario.minCash}</div>
                </div>
                <div>
                  <div className="text-muted-foreground capitalize">
                    coverage
                  </div>
                  <div className="font-semibold">{scenario.coverage}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Net Impact</div>
                  <div
                    className={`font-semibold ${
                      scenario.positiveImpact
                        ? "text-[var(--accent-success-500)]"
                        : "text-[var(--accent-destructive-500)]"
                    }`}
                  >
                    {scenario.netImpact}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="text-xs text-primary text-center w-full">
            +2 more scenarios — view all in Scenarios
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="outline" className="w-full">
              <Plus className="h-4 w-4 mr-1" />
              New Scenario
            </Button>
            <Button variant="outline" className="w-full">
              <ArrowLeftRight className="h-4 w-4 mr-1" />
              Compare
            </Button>
          </div>
        </CardContent>
      </Card>
    </ScaledThumbnail>
  );
}
